import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { throttle } from '@thd-olt-functional/utils';
import { useScrollLock } from './useMAScrollLock';
import {
  DRAWER_HEIGHTS,
  DRAG_THRESHOLD_PX,
  MINIMIZED_DRAG_MAX,
  OPEN_DRAG_MAX,
  TAP_DURATION_MS
} from '../components/MagicApronAssistant/constants';

/**
 * Custom hook to handle the state and logic of a draggable drawer
 *
 * @param { DrawerCommon } props - The properties for the useDraggableDrawer hook.
 * @returns {{
 *   handlePointerDown: (event: TouchEvent) => void,
 *   handlePointerMove: (event: TouchEvent) => void,
 *   handlePointerUp: () => void,
 *   dynamicHeight: string,
 * }}
 */
export const useDraggableDrawer = (drawerState, setDrawerState, currentHeight) => {
  /**
   * @type {[DrawerStatus, React.Dispatch<React.SetStateAction<DrawerStatus>>]}
   */
  const startY = useRef(null);
  const endY = useRef(null);
  const isDragging = useRef(false);
  const tapTimeout = useRef(null);
  const { lockScroll, unlockScroll } = useScrollLock();
  const [dragDistance, setDragDistance] = useState(0);
  const viewportHeight = useMemo(() => window.innerHeight, []);
  const [cachedHeight, setCachedHeight] = useState(null);

  const throttledCalculateHeight = throttle(
    16, // So UI animation is ~60fps
    (currentDragDistance) => {
      const dragRatio = Math.min(Math.abs(currentDragDistance / viewportHeight), 1.0) * Math.sign(currentDragDistance);
      const heightPercentage = (1 - dragRatio) * 100;
      const clampedHeight = Math.max(DRAWER_HEIGHTS.CLOSED, Math.min(heightPercentage, DRAWER_HEIGHTS.OPEN));
      // Adjust the height based on the drawer state max/min values
      const finalHeight = drawerState === 'MINIMIZED' ? Math.min(MINIMIZED_DRAG_MAX, clampedHeight)
        : Math.max(OPEN_DRAG_MAX, clampedHeight);
      const result = `${finalHeight}vh`;
      setCachedHeight(result);
      return result;
    });

  const dynamicHeight = useMemo(() => {
    if (drawerState === 'OPEN' && !dragDistance) {
      return 'calc(100% - 1em)';
    }
    if (!dragDistance) {
      return drawerState === 'MINIMIZED' ? currentHeight : `${currentHeight}vh`;
    }
    const newHeight = throttledCalculateHeight(dragDistance);
    return newHeight || cachedHeight || `${currentHeight}vh`;
  }, [drawerState, dragDistance, currentHeight, cachedHeight]);

  const handlePointerDown = useCallback((event) => {
    startY.current = event?.clientY || event.touches?.[0]?.clientY;
    endY.current = startY.current;
    lockScroll();

    tapTimeout.current = setTimeout(() => {
      if (startY.current !== null) {
        isDragging.current = true;
      }
    }, TAP_DURATION_MS);
  }, [lockScroll]);

  const handlePointerMove = useCallback((event) => {
    if (startY.current === null) return;

    endY.current = event?.clientY || event.touches?.[0]?.clientY;
    setDragDistance(endY.current);
  }, []);

  const handleTap = () => {
    setDrawerState(drawerState === 'OPEN' ? 'MINIMIZED' : 'OPEN');
    setDragDistance(null);
  };

  const handleDragEnd = useCallback(() => {
    if (startY.current !== null && endY.current !== null) {
      const deltaY = endY.current - startY.current;

      let newDrawerState = drawerState;
      if (drawerState === 'MINIMIZED') {
        if (deltaY >= (DRAG_THRESHOLD_PX / 2)) newDrawerState = 'CLOSED';
        if (deltaY <= -DRAG_THRESHOLD_PX) newDrawerState = 'OPEN';
      }
      if (drawerState === 'OPEN') {
        if (deltaY >= DRAG_THRESHOLD_PX) newDrawerState = 'MINIMIZED';
      }
      setDrawerState(newDrawerState);
      setDragDistance(null);
    }
  }, [drawerState]);

  const handlePointerUp = useCallback(() => {
    if (drawerState !== 'OPEN') {
      unlockScroll();
    }

    clearTimeout(tapTimeout.current);

    if (isDragging.current) {
      handleDragEnd();
    } else {
      handleTap();
    }

    startY.current = null;
    endY.current = null;
    isDragging.current = false;
  }, [unlockScroll, handleDragEnd, handleTap, drawerState]);

  useEffect(() => {
    if (drawerState === 'OPEN') {
      lockScroll();
    } else {
      unlockScroll();
    }
  }, [drawerState, lockScroll, unlockScroll]);

  // Needed to ensure scroll is unlocked if component is closed/unmounted
  useEffect(() => {
    return () => {
      unlockScroll();
    };
  }, [unlockScroll]);

  return ({
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    dynamicHeight,
  });
};
